import { useMemo, useState } from 'react';
import { Mail, Pencil, Phone, Plus } from 'lucide-react';

import { useSelector } from 'react-redux';
import { selectUserToken } from '../redux/features/user/userSelectors';
import { useGetContactsQuery } from '../redux/services/contacts/contactsApi';

import { Container } from '../layouts/Container';
import { Dialog } from '../components/ui/Dialog';
import { ErrorMsg } from '../components/ui/ErrorMsg';
import { Loader } from '../components/ui/Loader';
import { FloatingButton } from '../components/ui/buttons/FloatingButton';
import { AddContactForm } from '../components/form/forms/realtyForm/AddContactForm';

import { errorMessages } from '../data/constants';

const AVATAR_HUES = [265, 155, 35, 210, 330, 95];

const getFullName = contact =>
  `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim();

const getInitials = contact =>
  getFullName(contact)
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const getRealtiesCount = contact => {
  const count = contact?.realties?.length || 0;

  return `${count} ${count === 1 ? 'realty' : 'realties'}`;
};

const getLinkedLabel = contact =>
  contact?.realties?.length > 0 ? getRealtiesCount(contact) : 'Not linked';

const getDeleteWarning = contact =>
  contact?.realties?.length > 0
    ? `Linked to ${getRealtiesCount(contact)} — it will be removed from all of them.`
    : '';

const Avatar = ({ contact, size }) => {
  const hue = AVATAR_HUES[(contact?.id || 0) % AVATAR_HUES.length];

  return (
    <div
      className={
        size === 'lg'
          ? 'flex flex-none items-center justify-center w-[42px] h-[42px] rounded-full text-[15px] font-semibold'
          : 'flex flex-none items-center justify-center w-9 h-9 rounded-full text-[13px] font-semibold'
      }
      style={{
        background: `oklch(0.93 0.04 ${hue})`,
        color: `oklch(0.42 0.09 ${hue})`,
      }}
    >
      {getInitials(contact)}
    </div>
  );
};

const ContactValue = ({ icon: Icon, value }) => (
  <span className="flex items-center gap-1.5 min-w-0">
    <Icon
      size={14}
      className="flex-none text-[#6a6f78]"
    />
    <span className="truncate">{value}</span>
  </span>
);

const headerCellClasses =
  'py-2.5 px-2 first:pl-5 last:pr-5 text-left text-xs font-semibold uppercase tracking-[.03em] text-[#6a6f78]';
const bodyCellClasses = 'py-3 px-2 first:pl-5 last:pr-5 truncate';

export const Contacts = () => {
  const [query, setQuery] = useState('');
  const [editContactId, setEditContactId] = useState(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  const userToken = useSelector(selectUserToken);
  const {
    data: contacts = [],
    isLoading,
    error,
  } = useGetContactsQuery(
    { token: userToken, withRealties: true },
    { skip: !userToken }
  );

  const visibleContacts = useMemo(() => {
    const search = query.trim().toLowerCase();

    const filtered = search
      ? contacts.filter(contact =>
        [getFullName(contact), contact?.phone, contact?.email, contact?.company_name]
          .filter(Boolean)
          .some(value => value.toLowerCase().includes(search))
      )
      : contacts;

    return [...filtered].sort((a, b) =>
      getFullName(a).localeCompare(getFullName(b))
    );
  }, [contacts, query]);

  const isEmpty = !isLoading && !error && visibleContacts.length === 0;
  const emptyText = query.trim()
    ? `No contacts match “${query.trim()}”.`
    : 'No contacts yet.';

  return (
    <Container>
      <div className="flex flex-col gap-2.5 w-full min-h-full text-[#1f2328] bg-[#f6f7f9] rounded-md md:gap-[18px] md:py-7 md:px-8">
        {/* Header: sticky block on mobile, single row on desktop */}
        <div className="sticky top-0 z-[4] flex flex-col gap-2.5 pt-3.5 px-3.5 pb-2.5 bg-[#f6f7f9] md:static md:flex-row md:items-center md:gap-4 md:p-0">
          <div className="flex items-baseline gap-2 md:items-center md:gap-4">
            <h1 className="text-xl font-semibold md:text-[22px]">Contacts</h1>
            <span className="text-[13px] text-[#6a6f78] md:py-px md:px-2 md:bg-[#e9ebef] md:rounded-[10px]">
              {visibleContacts.length}
            </span>
          </div>

          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, phone, email or company"
            className="w-full h-11 px-3.5 text-[15px] bg-white border border-[#d9dce1] rounded-lg outline-none focus:border-[#8a91c0] md:ml-auto md:w-[380px] md:h-auto md:py-[9px] md:px-3 md:text-sm md:rounded-md"
          />
        </div>

        {isLoading && (
          <div className="flex justify-center w-full py-10">
            <Loader />
          </div>
        )}

        {error && (
          <div className="px-3.5 md:px-0">
            <ErrorMsg message={errorMessages.contacts} />
          </div>
        )}

        {/* Desktop: table */}
        {!isLoading && !error && (
          <div className="hidden overflow-hidden bg-white border border-[#e3e5e9] rounded-lg md:block">
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col style={{ width: '39%' }} />
                <col style={{ width: '26%' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '150px' }} />
              </colgroup>

              <thead className="bg-[#fafbfc] border-b border-[#e3e5e9]">
                <tr>
                  <th className={headerCellClasses}>Name</th>
                  <th className={headerCellClasses}>Phone</th>
                  <th className={headerCellClasses}>Email</th>
                  <th className={headerCellClasses}>Linked</th>
                </tr>
              </thead>

              <tbody>
                {visibleContacts.map(contact => (
                  // Row click is a mouse shortcut; the name button is the keyboard target
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
                  <tr
                    key={contact.id}
                    className="border-b border-[#eef0f2] cursor-pointer hover:bg-[#f7f8fb]"
                    onClick={() => setEditContactId(contact.id)}
                  >
                    <td className={bodyCellClasses}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar contact={contact} />
                        <button
                          type="button"
                          className="font-semibold text-left truncate"
                          onClick={e => {
                            e.stopPropagation();
                            setEditContactId(contact.id);
                          }}
                        >
                          {getFullName(contact) || '—'}
                        </button>
                      </div>
                    </td>
                    <td className={`${bodyCellClasses} text-[#30353c]`}>
                      {contact?.phone || '—'}
                    </td>
                    <td className={`${bodyCellClasses} text-[#30353c]`}>
                      {contact?.email || '—'}
                    </td>
                    <td className={bodyCellClasses}>
                      <span className="py-[3px] px-2.5 text-xs text-[#4a5190] bg-[#eceefa] rounded-xl">
                        {getLinkedLabel(contact)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {isEmpty && (
              <div className="py-7 px-5 text-sm text-[#6a6f78]">{emptyText}</div>
            )}
          </div>
        )}

        {/* Mobile: cards */}
        {!isLoading && !error && (
          <ul className="flex flex-col gap-2.5 pt-1 px-3.5 pb-[90px] md:hidden">
            {visibleContacts.map(contact => (
              <li
                key={contact.id}
                className="flex flex-col gap-3 p-3.5 bg-white border border-[#e3e5e9] rounded-[10px]"
              >
                <div className="flex items-center gap-3">
                  <Avatar
                    contact={contact}
                    size="lg"
                  />

                  <div className="flex flex-col flex-1 gap-[3px] min-w-0">
                    <span className="text-base font-semibold truncate">
                      {getFullName(contact) || '—'}
                    </span>
                    <span className="text-[13px] text-[#4a5190]">
                      {getLinkedLabel(contact)}
                    </span>
                  </div>

                  <button
                    type="button"
                    title="Edit contact"
                    className="flex flex-none items-center justify-center w-11 h-11 -my-1.5 -mr-1.5 rounded-lg text-[#6a6f78] hover:bg-[#eceefa] hover:text-[#3e4590] active:bg-[#eceefa] active:text-[#3e4590]"
                    onClick={() => setEditContactId(contact.id)}
                  >
                    <Pencil size={18} />
                  </button>
                </div>

                {(contact?.phone || contact?.email) && (
                  <div className="flex flex-col gap-1 pl-[54px] text-sm text-[#30353c]">
                    {contact?.phone && (
                      <ContactValue
                        icon={Phone}
                        value={contact.phone}
                      />
                    )}
                    {contact?.email && (
                      <ContactValue
                        icon={Mail}
                        value={contact.email}
                      />
                    )}
                  </div>
                )}
              </li>
            ))}

            {isEmpty && (
              <li className="py-5 px-1 text-sm text-[#6a6f78]">{emptyText}</li>
            )}
          </ul>
        )}
      </div>

      <FloatingButton
        icon={Plus}
        handleClick={() => setIsAddOpen(true)}
      />

      {isAddOpen && (
        <Dialog
          content={<AddContactForm handleClose={() => setIsAddOpen(false)} />}
          classes={'w-[290px] sm:w-[420px]'}
          isOpen={isAddOpen}
          onClose={() => setIsAddOpen(false)}
        />
      )}

      {editContactId && (
        <Dialog
          content={
            <AddContactForm
              isEdit={true}
              contactId={editContactId}
              deleteWarning={getDeleteWarning(
                contacts.find(contact => contact.id === editContactId)
              )}
              handleClose={() => setEditContactId(null)}
            />
          }
          classes={'w-[290px] sm:w-[420px]'}
          isOpen={!!editContactId}
          onClose={() => setEditContactId(null)}
        />
      )}
    </Container>
  );
};
